/**
 * Shared Formatting Utilities for TM Food / Galaxy One ERP
 * 
 * Bộ hàm định dạng dùng chung:
 * 1. Số & Tiền tệ (formatNumber, formatInteger, formatDecimal, formatCurrency, formatAmountInWords, formatPercent, formatCompactNumber, parseNumber)
 * 2. Ngày giờ (formatDate, formatDateTime, formatTime, formatDateVi, formatRelativeTime, formatDateRange, parseDate)
 * 3. Đo lường & Kích thước (formatQuantity, formatTemperature, formatFileSize)
 * 4. Chuỗi & Bảo mật (formatPhoneNumber, maskPhoneNumber, maskEmail, truncate)
 */

// ============================================================================
// 1. SỐ & TIỀN TỆ (NUMBERS & CURRENCY)
// ============================================================================

export interface FormatNumberOptions {
  /** Số chữ số thập phân tối đa (mặc định: giữ nguyên hoặc theo maxFractionDigits) */
  maxFractionDigits?: number;
  /** Số chữ số thập phân tối thiểu (mặc định: 0) */
  minFractionDigits?: number;
  /** Giá trị hiển thị khi input null, undefined hoặc NaN (mặc định: "—") */
  fallback?: string;
  /** Locale hiển thị (mặc định: "vi-VN") */
  locale?: string;
}

/**
 * Định dạng số với dấu phân cách hàng nghìn (chuẩn Việt Nam: dấu chấm phân tách nghìn, dấu phẩy phân tách thập phân).
 *
 * @example
 * formatNumber(1250000) // "1.250.000"
 * formatNumber(1234.567, 2) // "1.234,57"
 * formatNumber(null) // "—"
 */
export function formatNumber(
  value: number | string | null | undefined,
  options?: FormatNumberOptions | number,
): string {
  const opts: FormatNumberOptions =
    typeof options === "number" ? { maxFractionDigits: options } : options || {};

  const {
    maxFractionDigits,
    minFractionDigits,
    fallback = "—",
    locale = "vi-VN",
  } = opts;

  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const num = typeof value === "number" ? value : Number(String(value).trim());
  if (Number.isNaN(num)) {
    return fallback;
  }

  return num.toLocaleString(locale, {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  });
}

/**
 * Định dạng số nguyên (không có phần thập phân).
 *
 * @example
 * formatInteger(12345.67) // "12.346"
 * formatInteger(0) // "0"
 * formatInteger(null) // "—"
 */
export function formatInteger(
  value: number | string | null | undefined,
  fallback = "—",
): string {
  return formatNumber(value, {
    minFractionDigits: 0,
    maxFractionDigits: 0,
    fallback,
  });
}

/**
 * Định dạng số thập phân với số chữ số cố định sau dấu phẩy.
 *
 * @example
 * formatDecimal(12.3, 2) // "12,30"
 * formatDecimal(12.3456, 3) // "12,346"
 */
export function formatDecimal(
  value: number | string | null | undefined,
  decimals = 2,
  fallback = "—",
): string {
  return formatNumber(value, {
    minFractionDigits: decimals,
    maxFractionDigits: decimals,
    fallback,
  });
}

export interface FormatCurrencyOptions extends FormatNumberOptions {
  /** Mã tiền tệ ISO (VND, USD, EUR, JPY...) hoặc ký hiệu. Mặc định: "VND" */
  currency?: string;
  /**
   * Kiểu hiển thị ký hiệu:
   * - 'symbol': hiển thị ký hiệu (đ, $, €...)
   * - 'code': hiển thị mã tiền tệ (VND, USD...)
   * - 'none': chỉ lấy số, không kèm đơn vị tiền tệ
   * Mặc định: 'symbol' cho VND (đ), 'code' cho các ngoại tệ khác.
   */
  display?: "symbol" | "code" | "none";
}

/**
 * Định dạng tiền tệ linh hoạt, tự động tối ưu số thập phân theo đồng tiền:
 * - VND/đ: mặc định 0 chữ số thập phân, gắn "đ" (hoặc "VND" khi display=code).
 * - USD/EUR: mặc định 2 chữ số thập phân.
 *
 * @example
 * formatCurrency(1500000) // "1.500.000đ"
 * formatCurrency(1500000, "VND", { display: "code" }) // "1.500.000 VND"
 * formatCurrency(45.5, "USD") // "$45.50"
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currencyOrOptions?: string | FormatCurrencyOptions,
  extraOptions?: FormatCurrencyOptions,
): string {
  let currency = "VND";
  let options: FormatCurrencyOptions = {};

  if (typeof currencyOrOptions === "string") {
    currency = currencyOrOptions;
    options = extraOptions || {};
  } else if (currencyOrOptions && typeof currencyOrOptions === "object") {
    options = currencyOrOptions;
    if (options.currency) currency = options.currency;
  }

  const upperCurrency = currency.toUpperCase();
  const isVnd = upperCurrency === "VND" || upperCurrency === "VNĐ" || upperCurrency === "Đ" || upperCurrency === "₫";

  const defaultDecimals = isVnd ? 0 : 2;
  const maxFractionDigits = options.maxFractionDigits ?? defaultDecimals;
  const minFractionDigits = options.minFractionDigits ?? (isVnd ? 0 : maxFractionDigits);
  const fallback = options.fallback ?? "—";
  const display = options.display ?? "symbol";

  const formattedNum = formatNumber(value, {
    ...options,
    minFractionDigits,
    maxFractionDigits,
    fallback,
  });

  if (formattedNum === fallback) {
    return fallback;
  }

  if (display === "none") {
    return formattedNum;
  }

  if (isVnd) {
    return display === "symbol" ? `${formattedNum}đ` : `${formattedNum} VND`;
  }

  if (upperCurrency === "USD") {
    return display === "symbol" ? `$${formattedNum}` : `${formattedNum} USD`;
  }

  if (upperCurrency === "EUR") {
    return display === "symbol" ? `€${formattedNum}` : `${formattedNum} EUR`;
  }

  return `${formattedNum} ${upperCurrency}`;
}

/**
 * Số tiền viết bằng chữ (tiếng Việt) — khớp BE `VietnameseAmountWords.ToWords`.
 * Nội tệ: đơn vị "đồng". Ngoại tệ: major + phần lẻ (cent/…).
 *
 * @example
 * formatAmountInWords(2081600, "đ") // "Hai triệu tám mươi mốt nghìn sáu trăm đồng"
 * formatAmountInWords(123.45, "USD") // "Một trăm hai mươi ba đô la Mỹ và bốn mươi lăm cent"
 * formatAmountInWords(0.5, "$") // "Năm mươi cent"
 */
export function formatAmountInWords(
  value: number | string | null | undefined,
  currency?: string | null,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(String(value).trim());
  if (Number.isNaN(num)) return "—";

  const spec = resolveAmountCurrencyWords(currency);
  if (num < 0) return capitalizeVi(`không ${spec.major}`);

  const scale = 10 ** spec.decimals;
  const rounded = Math.round(num * scale) / scale;
  let whole = Math.floor(rounded);
  let fraction = Math.round((rounded - whole) * scale);
  if (fraction >= scale) {
    whole += 1;
    fraction = 0;
  }

  if (whole === 0 && fraction === 0) return capitalizeVi(`không ${spec.major}`);

  const parts: string[] = [];
  if (whole > 0) {
    const wholeWords = convertNumberGroup(whole).trim();
    if (wholeWords) parts.push(`${wholeWords} ${spec.major}`);
  } else if (fraction > 0 && !spec.minor) {
    parts.push(`không ${spec.major}`);
  }

  if (fraction > 0 && spec.minor) {
    const fracWords = convertNumberGroup(fraction).trim();
    if (fracWords) {
      parts.push(parts.length > 0 ? `và ${fracWords} ${spec.minor}` : `${fracWords} ${spec.minor}`);
    }
  }

  if (parts.length === 0) return capitalizeVi(`không ${spec.major}`);
  return capitalizeVi(parts.join(" "));
}

type AmountCurrencyWords = { major: string; minor: string | null; decimals: number };

function resolveAmountCurrencyWords(currency?: string | null): AmountCurrencyWords {
  if (!currency || !currency.trim()) return { major: "đồng", minor: null, decimals: 0 };
  const c = currency.trim();
  const upper = c.toUpperCase();

  if (upper === "VND" || upper === "VNĐ" || upper === "Đ" || upper === "₫" || c === "đ") {
    return { major: "đồng", minor: null, decimals: 0 };
  }
  if (upper === "USD" || c === "$") return { major: "đô la Mỹ", minor: "cent", decimals: 2 };
  if (upper === "EUR" || c === "€") return { major: "euro", minor: "cent", decimals: 2 };
  if (upper === "GBP" || c === "£") return { major: "bảng Anh", minor: "penny", decimals: 2 };
  if (upper === "JPY" || c === "¥") return { major: "yên", minor: null, decimals: 0 };
  if (upper === "CNY" || upper === "RMB") return { major: "nhân dân tệ", minor: "hào", decimals: 2 };
  if (upper === "KRW" || c === "₩") return { major: "won", minor: null, decimals: 0 };
  if (upper === "THB" || c === "฿") return { major: "baht", minor: "satang", decimals: 2 };
  if (upper === "AUD") return { major: "đô la Úc", minor: "cent", decimals: 2 };
  if (upper === "CAD") return { major: "đô la Canada", minor: "cent", decimals: 2 };
  if (upper === "SGD") return { major: "đô la Singapore", minor: "cent", decimals: 2 };
  return { major: c, minor: "cent", decimals: 2 };
}

const AMOUNT_ONES = [
  "không",
  "một",
  "hai",
  "ba",
  "bốn",
  "năm",
  "sáu",
  "bảy",
  "tám",
  "chín",
] as const;

function digitWord(d: number): string {
  return AMOUNT_ONES[Math.min(9, Math.max(0, Math.floor(d)))] ?? "không";
}

function convertNumberGroup(n: number): string {
  if (n === 0) return digitWord(0);
  const parts: string[] = [];
  let rest = Math.floor(Math.abs(n));
  const billion = Math.floor(rest / 1_000_000_000);
  rest %= 1_000_000_000;
  const million = Math.floor(rest / 1_000_000);
  rest %= 1_000_000;
  const thousand = Math.floor(rest / 1_000);
  const rem = rest % 1_000;

  if (billion > 0) parts.push(`${readTriple(billion)} tỷ`);
  if (million > 0) parts.push(`${readTriple(million)} triệu`);
  if (thousand > 0) parts.push(`${readTriple(thousand)} nghìn`);
  if (rem > 0) parts.push(readTriple(rem));
  return parts.join(" ");
}

function readTriple(n: number): string {
  const hundreds = Math.floor(n / 100);
  const tens = Math.floor((n % 100) / 10);
  const ones = n % 10;
  const parts: string[] = [];
  if (hundreds > 0) parts.push(`${digitWord(hundreds)} trăm`);
  if (tens > 1) {
    parts.push(`${digitWord(tens)} mươi`);
    if (ones === 1) parts.push("mốt");
    else if (ones === 5) parts.push("lăm");
    else if (ones > 0) parts.push(digitWord(ones));
  } else if (tens === 1) {
    parts.push("mười");
    if (ones === 5) parts.push("lăm");
    else if (ones > 0) parts.push(digitWord(ones));
  } else if (ones > 0) {
    if (hundreds > 0) parts.push("lẻ");
    parts.push(digitWord(ones));
  }
  return parts.join(" ");
}

function capitalizeVi(words: string): string {
  if (!words) return words;
  return words.charAt(0).toLocaleUpperCase("vi-VN") + words.slice(1);
}

export interface FormatPercentOptions {
  /**
   * Coi giá trị đầu vào là tỉ lệ (0.12 = 12%).
   * Mặc định: false (nếu đầu vào là 12 thì hiển thị "12%").
   */
  isRatio?: boolean;
  /** Số chữ số thập phân tối đa (mặc định: 1) */
  maxFractionDigits?: number;
  /** Số chữ số thập phân tối thiểu (mặc định: 0) */
  minFractionDigits?: number;
  /** Có hiển thị dấu '+' phía trước số dương không (ví dụ: +5,2%) */
  showPlusSign?: boolean;
  /** Fallback khi rỗng hoặc NaN (mặc định: "—") */
  fallback?: string;
}

/**
 * Định dạng phần trăm.
 *
 * @example
 * formatPercent(12.5) // "12,5%"
 * formatPercent(0.125, { isRatio: true }) // "12,5%"
 * formatPercent(5.4, { showPlusSign: true }) // "+5,4%"
 * formatPercent(-3.2, { showPlusSign: true }) // "-3,2%"
 */
export function formatPercent(
  value: number | string | null | undefined,
  options?: FormatPercentOptions,
): string {
  const {
    isRatio = false,
    maxFractionDigits = 1,
    minFractionDigits = 0,
    showPlusSign = false,
    fallback = "—",
  } = options || {};

  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const rawNum = typeof value === "number" ? value : Number(String(value).trim());
  if (Number.isNaN(rawNum)) {
    return fallback;
  }

  const finalNum = isRatio ? rawNum * 100 : rawNum;
  const formatted = finalNum.toLocaleString("vi-VN", {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  });

  const sign = showPlusSign && finalNum > 0 ? "+" : "";
  return `${sign}${formatted}%`;
}

/**
 * Rút gọn số lớn theo chuẩn Tiếng Việt (K, Tr - Triệu, Tỷ).
 *
 * @example
 * formatCompactNumber(1500) // "1,5K"
 * formatCompactNumber(2500000) // "2,5 Tr"
 * formatCompactNumber(3200000000) // "3,2 Tỷ"
 */
export function formatCompactNumber(
  value: number | string | null | undefined,
  fallback = "—",
): string {
  if (value === null || value === undefined || value === "") return fallback;
  const num = typeof value === "number" ? value : Number(String(value).trim());
  if (Number.isNaN(num)) return fallback;

  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (abs >= 1_000_000_000) {
    const val = abs / 1_000_000_000;
    return `${sign}${val.toLocaleString("vi-VN", { maximumFractionDigits: 1 })} Tỷ`;
  }
  if (abs >= 1_000_000) {
    const val = abs / 1_000_000;
    return `${sign}${val.toLocaleString("vi-VN", { maximumFractionDigits: 1 })} Tr`;
  }
  if (abs >= 1_000) {
    const val = abs / 1_000;
    return `${sign}${val.toLocaleString("vi-VN", { maximumFractionDigits: 1 })}K`;
  }

  return `${sign}${abs.toLocaleString("vi-VN", { maximumFractionDigits: 1 })}`;
}

/**
 * Chuyển đổi chuỗi số (có dấu chấm/phẩy) về số `number` an toàn.
 * Hỗ trợ cả định dạng Việt Nam (1.234.567,89) và Quốc tế (1,234,567.89).
 *
 * @example
 * parseNumber("1.250.000,5") // 1250000.5
 * parseNumber("1,250.50") // 1250.5
 * parseNumber("abc") // null
 */
export function parseNumber(val: string | number | null | undefined): number | null {
  if (val === null || val === undefined || val === "") return null;
  if (typeof val === "number") return Number.isNaN(val) ? null : val;

  const trimmed = val.trim();
  if (!trimmed) return null;

  // Xử lý dấu phân tách
  // Nếu có cả dấu '.' và dấu ','
  if (trimmed.includes(".") && trimmed.includes(",")) {
    const lastDot = trimmed.lastIndexOf(".");
    const lastComma = trimmed.lastIndexOf(",");
    if (lastComma > lastDot) {
      // Định dạng VN: 1.250.000,50 -> loại bỏ '.', thay ',' bằng '.'
      const normalized = trimmed.replace(/\./g, "").replace(",", ".");
      const n = Number(normalized);
      return Number.isNaN(n) ? null : n;
    } else {
      // Định dạng US: 1,250,000.50 -> loại bỏ ','
      const normalized = trimmed.replace(/,/g, "");
      const n = Number(normalized);
      return Number.isNaN(n) ? null : n;
    }
  }

  // Chỉ có dấu phẩy: ví dụ "12,5" (thập phân VN) hoặc "1,000"
  if (trimmed.includes(",")) {
    // Nếu chỉ có 1 dấu phẩy và cách cuối 1-2 số -> thường là số thập phân VN ("12,5")
    const parts = trimmed.split(",");
    if (parts.length === 2 && parts[1] !== undefined && parts[1].length <= 3) {
      const normalized = `${parts[0]}.${parts[1]}`;
      const n = Number(normalized);
      return Number.isNaN(n) ? null : n;
    }
    // Nếu nhiều dấu phẩy: "1,000,000" -> loại bỏ phẩy
    const normalized = trimmed.replace(/,/g, "");
    const n = Number(normalized);
    return Number.isNaN(n) ? null : n;
  }

  // Chỉ có dấu chấm hoặc không có dấu: ví dụ "1250000" hoặc "12.5" hoặc "1.250.000"
  if (trimmed.includes(".")) {
    const parts = trimmed.split(".");
    // Nếu có nhiều hơn 1 dấu chấm -> dấu phân cách hàng nghìn VN ("1.250.000")
    if (parts.length > 2) {
      const normalized = trimmed.replace(/\./g, "");
      const n = Number(normalized);
      return Number.isNaN(n) ? null : n;
    }
  }

  const n = Number(trimmed);
  return Number.isNaN(n) ? null : n;
}

// ============================================================================
// 2. NGÀY THÁNG & THỜI GIAN (DATES & TIMES)
// ============================================================================

export type DateInput = Date | string | number | null | undefined;

/**
 * Phân tích chuỗi ngày / số timestamp / đối tượng Date thành đối tượng `Date` hợp lệ.
 * Tránh lỗi lệch múi giờ khi parse chuỗi thuần "YYYY-MM-DD" hoặc "DD/MM/YYYY".
 */
export function parseDate(input: DateInput): Date | null {
  if (!input) return null;
  if (input instanceof Date) {
    return Number.isNaN(input.getTime()) ? null : input;
  }

  if (typeof input === "number") {
    // Hỗ trợ cả timestamp theo giây (10 số) và mili-giây (13 số)
    const ms = input < 10_000_000_000 ? input * 1000 : input;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const str = String(input).trim();
  if (!str) return null;

  // Định dạng DD/MM/YYYY hoặc DD/MM/YYYY HH:mm[:ss]
  if (str.includes("/")) {
    const [datePart, timePart] = str.split(" ");
    if (datePart) {
      const [d, m, y] = datePart.split("/").map(Number);
      if (d && m && y) {
        const date = new Date(y, m - 1, d);
        if (timePart) {
          const [hh, mm, ss] = timePart.split(":").map(Number);
          date.setHours(hh || 0, mm || 0, ss || 0, 0);
        }
        return Number.isNaN(date.getTime()) ? null : date;
      }
    }
  }

  // Định dạng thuần YYYY-MM-DD (10 ký tự, không có giờ) -> Giữ nguyên giờ địa phương tránh bị lùi ngày do UTC
  const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (ymdMatch && ymdMatch[1] && ymdMatch[2] && ymdMatch[3]) {
    const y = Number(ymdMatch[1]);
    const m = Number(ymdMatch[2]);
    const d = Number(ymdMatch[3]);
    const date = new Date(y, m - 1, d);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // Các định dạng ISO khác: "2026-09-30T08:50:00Z"
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : d;
}

export interface FormatDateOptions {
  /** Ký tự phân cách (mặc định: '/') */
  separator?: "/" | "-" | ".";
  /** Fallback khi rỗng hoặc không hợp lệ (mặc định: "—") */
  fallback?: string;
}

/**
 * Định dạng ngày (mặc định theo chuẩn Việt Nam: DD/MM/YYYY).
 *
 * @example
 * formatDate("2026-09-30") // "30/09/2026"
 * formatDate(new Date(), { separator: "-" }) // "30-09-2026"
 * formatDate(null) // "—"
 */
export function formatDate(
  input: DateInput,
  optionsOrSeparator?: FormatDateOptions | string,
): string {
  const options: FormatDateOptions =
    typeof optionsOrSeparator === "string"
      ? { separator: optionsOrSeparator as "/" | "-" | "." }
      : optionsOrSeparator || {};

  const { separator = "/", fallback = "—" } = options;

  const d = parseDate(input);
  if (!d) return fallback;

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}${separator}${month}${separator}${year}`;
}

/**
 * Danh mục Enum định dạng Ngày Giờ chuẩn trong hệ thống ERP TM Food / Galaxy One.
 * Mỗi giá trị tương ứng với một mã số hoặc tên kiểu định dạng:
 * - 1: DATE (DD/MM/YYYY)
 * - 2: DATETIME (DD/MM/YYYY HH:mm — Ngày trước, giờ sau)
 * - 3: DATETIME_SECONDS (DD/MM/YYYY HH:mm:ss — Ngày trước, giờ sau có giây cho Timeline/Audit)
 * - 4: TIME (HH:mm)
 * - 5: TIME_SECONDS (HH:mm:ss)
 * - 6: ISO_DATE (YYYY-MM-DD)
 * - 7: ISO_DATETIME (YYYY-MM-DD HH:mm:ss)
 * - 8: DATE_DASH (DD-MM-YYYY)
 * - 9: DATETIME_DASH (DD-MM-YYYY HH:mm)
 * - 10: DATETIME_DASH_SECONDS (DD-MM-YYYY HH:mm:ss)
 */
export enum DateTimeFormat {
  /** 1: DD/MM/YYYY — Ngày/Tháng/Năm (vd: 13/10/2026) */
  DATE = 1,
  /** 2: DD/MM/YYYY HH:mm — Ngày trước, giờ sau không giây (Chuẩn chung Galaxy ERP) (vd: 13/10/2026 15:30) */
  DATETIME = 2,
  /** 3: DD/MM/YYYY HH:mm:ss — Ngày trước, giờ sau có giây (Timeline, Lịch sử thao tác, Audit log) (vd: 13/10/2026 15:30:45) */
  DATETIME_SECONDS = 3,
  /** 4: HH:mm — Giờ:Phút (vd: 15:30) */
  TIME = 4,
  /** 5: HH:mm:ss — Giờ:Phút:Giây (vd: 15:30:45) */
  TIME_SECONDS = 5,
  /** 6: YYYY-MM-DD — Chuẩn ISO Date (dùng cho input date / query params) (vd: 2026-10-13) */
  ISO_DATE = 6,
  /** 7: YYYY-MM-DD HH:mm:ss — Chuẩn ISO DateTime (vd: 2026-10-13 15:30:45) */
  ISO_DATETIME = 7,
  /** 8: DD-MM-YYYY — Ngày phân cách gạch ngang (vd: 13-10-2026) */
  DATE_DASH = 8,
  /** 9: DD-MM-YYYY HH:mm — Ngày trước giờ sau gạch ngang (vd: 13-10-2026 15:30) */
  DATETIME_DASH = 9,
  /** 10: DD-MM-YYYY HH:mm:ss — Ngày trước giờ sau gạch ngang có giây (vd: 13-10-2026 15:30:45) */
  DATETIME_DASH_SECONDS = 10,
}

export const DATE_TIME_PRESET_PATTERNS: Record<DateTimeFormat, string> = {
  [DateTimeFormat.DATE]: "DD/MM/YYYY",
  [DateTimeFormat.DATETIME]: "DD/MM/YYYY HH:mm",
  [DateTimeFormat.DATETIME_SECONDS]: "DD/MM/YYYY HH:mm:ss",
  [DateTimeFormat.TIME]: "HH:mm",
  [DateTimeFormat.TIME_SECONDS]: "HH:mm:ss",
  [DateTimeFormat.ISO_DATE]: "YYYY-MM-DD",
  [DateTimeFormat.ISO_DATETIME]: "YYYY-MM-DD HH:mm:ss",
  [DateTimeFormat.DATE_DASH]: "DD-MM-YYYY",
  [DateTimeFormat.DATETIME_DASH]: "DD-MM-YYYY HH:mm",
  [DateTimeFormat.DATETIME_DASH_SECONDS]: "DD-MM-YYYY HH:mm:ss",
};

export type DateTimeFormatInput =
  | DateTimeFormat
  | keyof typeof DateTimeFormat
  | (string & {})
  | number;

/**
 * Định dạng ngày giờ linh hoạt theo Preset Enum (số hoặc tên enum) hoặc chuỗi format mẫu (pattern).
 *
 * @param input Date, chuỗi ISO, timestamp hoặc null/undefined
 * @param format Số Enum (1, 2, 3...), Enum (DateTimeFormat.DATETIME_SECONDS), tên Enum ("DATETIME_SECONDS") hoặc chuỗi format ("DD/MM/YYYY HH:mm:ss"). Mặc định: DateTimeFormat.DATETIME (2)
 * @param fallback Chuỗi hiển thị khi input null, undefined hoặc không hợp lệ (mặc định: "—")
 *
 * @example
 * formatDateTimePreset(new Date(), DateTimeFormat.DATETIME) // "13/10/2026 15:30"
 * formatDateTimePreset(new Date(), DateTimeFormat.DATETIME_SECONDS) // "13/10/2026 15:30:45"
 * formatDateTimePreset(new Date(), 3) // "13/10/2026 15:30:45"
 * formatDateTimePreset(new Date(), "DD/MM/YYYY HH:mm:ss") // "13/10/2026 15:30:45"
 * formatDateTimePreset(new Date(), DateTimeFormat.DATE) // "13/10/2026"
 * formatDateTimePreset(null) // "—"
 */
export function formatDateTimePreset(
  input: DateInput,
  format: DateTimeFormatInput = DateTimeFormat.DATETIME,
  fallback = "—",
): string {
  const d = parseDate(input);
  if (!d) return fallback;

  let pattern: string | undefined;

  if (typeof format === "number") {
    pattern = DATE_TIME_PRESET_PATTERNS[format as DateTimeFormat];
  } else if (typeof format === "string") {
    const enumVal = DateTimeFormat[format as keyof typeof DateTimeFormat];
    if (typeof enumVal === "number") {
      pattern = DATE_TIME_PRESET_PATTERNS[enumVal];
    } else {
      pattern = format;
    }
  }

  if (!pattern) {
    pattern = DATE_TIME_PRESET_PATTERNS[DateTimeFormat.DATETIME];
  }

  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  const pad2 = (n: number) => String(n).padStart(2, "0");

  const tokens: Record<string, string> = {
    YYYY: String(year),
    YY: String(year).slice(-2),
    MM: pad2(month),
    M: String(month),
    DD: pad2(day),
    D: String(day),
    HH: pad2(hours),
    H: String(hours),
    mm: pad2(minutes),
    m: String(minutes),
    ss: pad2(seconds),
    s: String(seconds),
  };

  return pattern.replace(/(YYYY|YY|MM|M|DD|D|HH|H|mm|m|ss|s)/g, (match) => {
    return tokens[match] ?? match;
  });
}

export interface FormatDateTimeOptions extends FormatDateOptions {
  /** Hiển thị cả giây hay không (mặc định: false) */
  showSeconds?: boolean;
  /** Ký tự nối giữa ngày và giờ (mặc định: " ") */
  timeSeparator?: string;
}

/**
 * Định dạng ngày và giờ (mặc định: DD/MM/YYYY HH:mm).
 * Hỗ trợ truyền options object HOẶC số Enum / tên Enum / chuỗi format pattern.
 *
 * @example
 * formatDateTime("2026-09-30T08:45:00") // "30/09/2026 08:45"
 * formatDateTime(new Date(), { showSeconds: true }) // "30/09/2026 08:45:20"
 * formatDateTime(new Date(), DateTimeFormat.DATETIME_SECONDS) // "30/09/2026 08:45:20"
 * formatDateTime(new Date(), 3) // "30/09/2026 08:45:20"
 */
export function formatDateTime(
  input: DateInput,
  optionsOrFormat?: FormatDateTimeOptions | DateTimeFormatInput,
): string {
  if (
    typeof optionsOrFormat === "number" ||
    (typeof optionsOrFormat === "string" && optionsOrFormat.length > 0)
  ) {
    return formatDateTimePreset(input, optionsOrFormat);
  }

  const {
    separator = "/",
    fallback = "—",
    showSeconds = false,
    timeSeparator = " ",
  } = (optionsOrFormat as FormatDateTimeOptions) || {};

  const d = parseDate(input);
  if (!d) return fallback;

  const dateStr = formatDate(d, { separator, fallback });
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  if (showSeconds) {
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${dateStr}${timeSeparator}${hours}:${minutes}:${seconds}`;
  }

  return `${dateStr}${timeSeparator}${hours}:${minutes}`;
}

/**
 * Định dạng giờ (mặc định: HH:mm).
 *
 * @example
 * formatTime("2026-09-30T14:30:00") // "14:30"
 * formatTime(new Date(), true) // "14:30:15"
 */
export function formatTime(
  input: DateInput,
  showSeconds = false,
  fallback = "—",
): string {
  const d = parseDate(input);
  if (!d) return fallback;

  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  if (showSeconds) {
    const seconds = String(d.getSeconds()).padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  return `${hours}:${minutes}`;
}

const VI_WEEKDAYS = [
  "Chủ Nhật",
  "Thứ Hai",
  "Thứ Ba",
  "Thứ Tư",
  "Thứ Năm",
  "Thứ Sáu",
  "Thứ Bảy",
];

/**
 * Định dạng ngày chi tiết bằng Tiếng Việt.
 *
 * @example
 * formatDateVi("2026-09-30") // "30 tháng 09, 2026"
 * formatDateVi("2026-09-30", { withWeekday: true }) // "Thứ Tư, 30/09/2026"
 */
export function formatDateVi(
  input: DateInput,
  options?: { withWeekday?: boolean; fallback?: string },
): string {
  const { withWeekday = false, fallback = "—" } = options || {};
  const d = parseDate(input);
  if (!d) return fallback;

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  if (withWeekday) {
    const weekday = VI_WEEKDAYS[d.getDay()];
    return `${weekday}, ${day}/${month}/${year}`;
  }

  return `${day} tháng ${month}, ${year}`;
}

/**
 * Định dạng thời gian tương đối ("Vừa xong", "5 phút trước", "Hôm qua"...).
 * Rất thích hợp cho Activity Log, Thông báo, Lịch sử thao tác.
 *
 * @example
 * formatRelativeTime(Date.now() - 30000) // "Vừa xong"
 * formatRelativeTime(Date.now() - 5 * 60 * 1000) // "5 phút trước"
 * formatRelativeTime(Date.now() - 2 * 3600 * 1000) // "2 giờ trước"
 */
export function formatRelativeTime(
  input: DateInput,
  baseDateInput?: DateInput,
  fallback = "—",
): string {
  const target = parseDate(input);
  if (!target) return fallback;

  const base = parseDate(baseDateInput) || new Date();
  const diffMs = base.getTime() - target.getTime();
  const isPast = diffMs >= 0;
  const absDiffSec = Math.floor(Math.abs(diffMs) / 1000);

  if (absDiffSec < 45) {
    return isPast ? "Vừa xong" : "Ngay bây giờ";
  }

  const mins = Math.floor(absDiffSec / 60);
  if (mins < 60) {
    return isPast ? `${mins} phút trước` : `${mins} phút nữa`;
  }

  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    return isPast ? `${hours} giờ trước` : `${hours} giờ nữa`;
  }

  const days = Math.floor(hours / 24);
  if (days === 1) {
    return isPast ? "Hôm qua" : "Ngày mai";
  }
  if (days < 30) {
    return isPast ? `${days} ngày trước` : `${days} ngày nữa`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return isPast ? `${months} tháng trước` : `${months} tháng nữa`;
  }

  const years = Math.floor(days / 365);
  return isPast ? `${years} năm trước` : `${years} năm nữa`;
}

/**
 * Định dạng khoảng ngày (Date Range).
 *
 * @example
 * formatDateRange("2026-09-01", "2026-09-30") // "01/09/2026 – 30/09/2026"
 * formatDateRange("2026-09-30", "2026-09-30") // "30/09/2026"
 */
export function formatDateRange(
  start: DateInput,
  end: DateInput,
  options?: { fallback?: string; withTime?: boolean; separator?: string },
): string {
  const { fallback = "—", withTime = false, separator = " – " } = options || {};
  const dStart = parseDate(start);
  const dEnd = parseDate(end);

  if (!dStart && !dEnd) return fallback;
  if (dStart && !dEnd) return withTime ? formatDateTime(dStart) : formatDate(dStart);
  if (!dStart && dEnd) return withTime ? formatDateTime(dEnd) : formatDate(dEnd);

  const startStr = withTime ? formatDateTime(dStart) : formatDate(dStart);
  const endStr = withTime ? formatDateTime(dEnd) : formatDate(dEnd);

  if (startStr === endStr) {
    return startStr;
  }

  return `${startStr}${separator}${endStr}`;
}

// ============================================================================
// 3. ĐO LƯỜNG, ĐƠN VỊ & FILE (MEASUREMENTS, UNITS & FILES)
// ============================================================================

/**
 * Định dạng số lượng đi kèm đơn vị tính (UOM) (đặc thù ngành Thực phẩm / Kho / Sản xuất TM Food).
 *
 * @example
 * formatQuantity(1500, "kg") // "1.500 kg"
 * formatQuantity(25.45, "thùng", 2) // "25,45 thùng"
 * formatQuantity(null) // "—"
 */
export function formatQuantity(
  qty: number | string | null | undefined,
  uom?: string | null,
  optionsOrDecimals?: FormatNumberOptions | number,
): string {
  const opts: FormatNumberOptions =
    typeof optionsOrDecimals === "number"
      ? { maxFractionDigits: optionsOrDecimals }
      : optionsOrDecimals || { maxFractionDigits: 3 };

  const formattedNum = formatNumber(qty, opts);
  if (formattedNum === (opts.fallback ?? "—")) {
    return opts.fallback ?? "—";
  }

  return uom ? `${formattedNum} ${uom.trim()}` : formattedNum;
}

/**
 * Định dạng nhiệt độ bảo quản kho lạnh / xe đông mát (đặc thù TM Food).
 *
 * @example
 * formatTemperature(-18) // "-18°C"
 * formatTemperature(4.5) // "4,5°C"
 * formatTemperature(null) // "—"
 */
export function formatTemperature(
  temp: number | string | null | undefined,
  unit = "°C",
  fallback = "—",
): string {
  if (temp === null || temp === undefined || temp === "") return fallback;
  const num = typeof temp === "number" ? temp : Number(String(temp).trim());
  if (Number.isNaN(num)) return fallback;

  return `${num.toLocaleString("vi-VN", { maximumFractionDigits: 1 })}${unit}`;
}

/**
 * Định dạng kích thước dung lượng tập tin (Bytes -> KB, MB, GB).
 * Dùng cho FileUpload, đính kèm chứng từ, hình ảnh, tài liệu.
 *
 * @example
 * formatFileSize(1024) // "1 KB"
 * formatFileSize(5242880) // "5 MB"
 * formatFileSize(1572864) // "1,5 MB"
 */
export function formatFileSize(
  bytes: number | null | undefined,
  decimals = 1,
  fallback = "0 B",
): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes) || bytes < 0) {
    return fallback;
  }
  if (bytes === 0) return "0 B";

  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const safeIndex = Math.min(i, sizes.length - 1);
  const val = bytes / Math.pow(k, safeIndex);

  const formatted = val.toLocaleString("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: safeIndex === 0 ? 0 : decimals,
  });

  return `${formatted} ${sizes[safeIndex]}`;
}

// ============================================================================
// 4. CHUỖI & THÔNG TIN LIÊN HỆ (STRINGS & CONTACTS)
// ============================================================================

/**
 * Định dạng số điện thoại Việt Nam (10 số).
 *
 * @example
 * formatPhoneNumber("0901234567") // "0901 234 567"
 * formatPhoneNumber("84901234567") // "0901 234 567"
 * formatPhoneNumber("+84901234567") // "0901 234 567"
 */
export function formatPhoneNumber(
  phone: string | null | undefined,
  fallback = "—",
): string {
  if (!phone) return fallback;
  const clean = phone.replace(/\D/g, "");
  if (!clean) return fallback;

  // Chuẩn hóa 84... thành 0...
  let normalized = clean;
  if (normalized.startsWith("84") && normalized.length === 11) {
    normalized = "0" + normalized.slice(2);
  }

  // Định dạng di động 10 số: 0xxx xxx xxx (ví dụ 0901 234 567)
  if (normalized.length === 10) {
    return `${normalized.slice(0, 4)} ${normalized.slice(4, 7)} ${normalized.slice(7)}`;
  }

  // Định dạng điện thoại bàn / 11 số: 028 xxxx xxxx
  if (normalized.length === 11) {
    return `${normalized.slice(0, 3)} ${normalized.slice(3, 7)} ${normalized.slice(7)}`;
  }

  return phone;
}

/**
 * Che một phần số điện thoại vì lý do bảo mật / riêng tư.
 *
 * @example
 * maskPhoneNumber("0901234567") // "0901***567"
 */
export function maskPhoneNumber(
  phone: string | null | undefined,
  fallback = "—",
): string {
  if (!phone) return fallback;
  const clean = phone.replace(/\D/g, "");
  if (clean.length < 7) return phone;

  const start = clean.slice(0, 4);
  const end = clean.slice(-3);
  return `${start}***${end}`;
}

/**
 * Che email để bảo mật thông tin tài khoản người dùng / khách hàng.
 *
 * @example
 * maskEmail("quangnam1617@gmail.com") // "q***7@gmail.com"
 * maskEmail("admin@tmfood.vn") // "a***n@tmfood.vn"
 */
export function maskEmail(
  email: string | null | undefined,
  fallback = "—",
): string {
  if (!email || !email.includes("@")) return fallback;
  const [name, domain] = email.split("@");
  if (!name || !domain) return email;

  if (name.length <= 2) {
    return `${name[0]}*@${domain}`;
  }

  const firstChar = name[0];
  const lastChar = name[name.length - 1];
  return `${firstChar}***${lastChar}@${domain}`;
}

/**
 * Cắt ngắn văn bản an toàn kèm dấu 3 chấm "...".
 *
 * @example
 * truncate("Công ty Cổ phần Thực phẩm TM Food Galaxy", 20) // "Công ty Cổ phần T..."
 */
export function truncate(
  str: string | null | undefined,
  maxLength: number,
  ellipsis = "...",
): string {
  if (!str) return "";
  if (str.length <= maxLength) return str;
  return str.slice(0, Math.max(0, maxLength - ellipsis.length)) + ellipsis;
}
